import { FormApi } from 'final-form'
import { CreateQuestionInput, QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import { findOneQuestion_findOneQuestion as Question } from '__generatedTypes__/findOneQuestion'
import {
  findAllWithSubjectsTabs_findAllWithSubjectsTabs as BasePlatform,
  findAllWithSubjectsTabs_findAllWithSubjectsTabs_subjects as BaseSubject,
} from '__generatedTypes__/findAllWithSubjectsTabs'
import { PlatformSlug } from 'constants/consts'
import { ImageField } from 'components/DropzoneField'
import { EMPTY_ANSWER } from './AnswersFieldArray'
import { RxWritingAnswer } from './RxWritingAnswers/helpers'
import { NumericResponseAnswer } from './NumericResponseAnswer/helpers'
import { TextCompletionBlank } from './TextCompletionAnswers/helpers'
import { initializeJsonTable, prepareJsonTable } from './AdditionalFields/TableFields/tableUtils'

export type { findAllQuestionCategory_findAllQuestionCategory_items as QuestionCategory } from '__generatedTypes__/findAllQuestionCategory'
export type {
  findAllWithSubjectsTabs_findAllWithSubjectsTabs as BasePlatform,
  findAllWithSubjectsTabs_findAllWithSubjectsTabs_subjects as BaseSubject,
} from '__generatedTypes__/findAllWithSubjectsTabs'

export type Platform = Pick<BasePlatform, 'id' | 'slug' | 'title'>

export type Subject = Pick<BaseSubject, 'id' | 'slug' | 'tabName'> & {
  platformSlug: PlatformSlug
  isSingle?: boolean
}

export type SubjectGroup = {
  id: string
  slug: string
  title: string
  isSingle: boolean
  subjects: Subject[]
}

export type { RxWritingAnswer } from './RxWritingAnswers/helpers'
export type { NumericResponseAnswer } from './NumericResponseAnswer/helpers'
export type { TextCompletionBlank, TextCompletionOption } from './TextCompletionAnswers/helpers'

export type FormState = Question & {
  rxWritingAnswer: RxWritingAnswer[]
  numericResponseAnswer: NumericResponseAnswer
  textCompletionAnswer: TextCompletionBlank[]
  textCompletionBlanksCount?: { id: string; title: string }
  platforms?: Platform[]
  subjectGroup?: SubjectGroup
  questionType?: QuestionType
  resetStatistics?: boolean
  exhibitFile?: ImageField[]
  xRayFile?: ImageField[]
  photoFile?: ImageField[]
  periodontalChartFile?: ImageField[]
  panoramicChartFile?: ImageField[]
  dentalChartFile?: ImageField[]
  isNeedAdditionalInformation?: boolean
  isNeedExhibitFile?: boolean
  isNeedXRayFile?: boolean
  isNeedPhotoFile?: boolean
  isNeedPatientBox?: boolean
  isNeedDentalChartFile?: boolean
  isNeedPeriodontalChartFile?: boolean
  isNeedPanoramicChartFile?: boolean
  isNeedModel3D?: boolean
  isNeedEndodonticTable?: boolean
  isNeedSimpleEndodonticTable?: boolean
  isNeedOrthodonticTable?: boolean
  isNeedPeriodontalTable?: boolean
  isNeedSourcesAndCredits?: boolean
  simpleEndodonticTable?: string
  showMultipleSelectionLabel?: boolean
}

export type FormStateApi = FormApi<FormState, Partial<FormState>>

export const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

export type OtherFile = {
  key: string
  isNeedKey: string
  variant: 'images' | 'text' | 'html'
  label: string
  placeholder?: string
  description?: string
  filesLimit?: number
}

export type TableApiKey = keyof Pick<CreateQuestionInput, 'endodonticTable' | 'orthodonticTable' | 'periodontalTable'>

export type TableField = {
  apiKey: TableApiKey
  formKey: keyof FormState
  isNeedKey: keyof FormState
  label: string
  description?: string
  initialize: (value: unknown) => string | undefined
  prepare: (formValue: unknown) => unknown | null
}

export const SOURCES_AND_CREDITS_CONFIG: OtherFile = {
  key: 'sourcesAndCredits',
  isNeedKey: 'isNeedSourcesAndCredits',
  label: 'Sources and Credits',
  placeholder: 'Enter Sources and Credits',
  variant: 'html',
}

export const INBDE_FILES: OtherFile[] = [
  {
    key: 'exhibitFile',
    isNeedKey: 'isNeedExhibitFile',
    variant: 'images',
    label: 'Exhibit',
  },
  {
    key: 'xRayFile',
    isNeedKey: 'isNeedXRayFile',
    variant: 'images',
    label: 'X-rays',
  },
  {
    key: 'photoFile',
    isNeedKey: 'isNeedPhotoFile',
    variant: 'images',
    label: 'Photo',
  },
]

export const DLOSCE_FILES: OtherFile[] = [
  {
    key: 'xRayFile',
    isNeedKey: 'isNeedXRayFile',
    variant: 'images',
    label: 'X-rays',
  },
  {
    key: 'photoFile',
    isNeedKey: 'isNeedPhotoFile',
    variant: 'images',
    label: 'Photo',
  },
  {
    key: 'model3D',
    isNeedKey: 'isNeedModel3D',
    label: '3D Model',
    variant: 'text',
  },
]

export const NBDHE_FILES: OtherFile[] = [
  {
    key: 'dentalChartFile',
    isNeedKey: 'isNeedDentalChartFile',
    variant: 'images',
    label: 'Dental chart',
  },
  {
    key: 'periodontalChartFile',
    isNeedKey: 'isNeedPeriodontalChartFile',
    variant: 'images',
    label: 'Periodontal chart',
  },
  {
    key: 'panoramicChartFile',
    isNeedKey: 'isNeedPanoramicChartFile',
    variant: 'images',
    label: 'Panoramic radiographic',
  },
  {
    key: 'xRayFile',
    isNeedKey: 'isNeedXRayFile',
    variant: 'images',
    label: 'Radiograph',
  },
  {
    key: 'photoFile',
    isNeedKey: 'isNeedPhotoFile',
    variant: 'images',
    label: 'Photo',
  },
]

export const MULTI_NDEB_FILES: OtherFile[] = [
  // TODO: should be replaced with endodonticTable, orthodonticTable and periodontalTable
  {
    key: 'dentalChartFile',
    isNeedKey: 'isNeedDentalChartFile',
    variant: 'images',
    label: 'Additional Chart or Table (DEPRECATED)',
    description:
      'This field is deprecated. Please use Clinical Table, Orthodontic Table and Periodontal Table instead.',
  },
  {
    key: 'additionalInformation',
    isNeedKey: 'isNeedAdditionalInformation',
    label: 'Additional Information',
    placeholder: 'Enter Additional Information',
    variant: 'html',
  },
  {
    key: 'photoFile',
    isNeedKey: 'isNeedPhotoFile',
    variant: 'images',
    label: 'Photos',
    filesLimit: 10,
  },
  {
    key: 'model3D',
    isNeedKey: 'isNeedModel3D',
    label: '3D Model',
    variant: 'text',
  },
]

export const MULTI_NDEB_TABLE_FIELDS: TableField[] = [
  {
    apiKey: 'endodonticTable',
    formKey: 'endodonticTable',
    isNeedKey: 'isNeedEndodonticTable',
    label: 'Endodontic Table',
    initialize: initializeJsonTable,
    prepare: prepareJsonTable,
  },
  {
    apiKey: 'orthodonticTable',
    formKey: 'orthodonticTable',
    isNeedKey: 'isNeedOrthodonticTable',
    label: 'Orthodontic Table',
    initialize: initializeJsonTable,
    prepare: prepareJsonTable,
  },
  {
    apiKey: 'periodontalTable',
    formKey: 'periodontalTable',
    isNeedKey: 'isNeedPeriodontalTable',
    label: 'Periodontal Table',
    initialize: initializeJsonTable,
    prepare: prepareJsonTable,
  },
]

export const MULTI_DLOSCE_TABLE_FIELDS: TableField[] = [
  {
    apiKey: 'endodonticTable',
    formKey: 'simpleEndodonticTable',
    isNeedKey: 'isNeedSimpleEndodonticTable',
    label: 'Endodontic Table',
    initialize: initializeJsonTable,
    prepare: prepareJsonTable,
  },
]

export type PatientBoxField = {
  key: string
  label: string
  required?: boolean
}

export const PATIENT_HISTORY_FIELDS: PatientBoxField[] = [
  {
    key: 'patient',
    label: 'Patient',
    required: true,
  },
  {
    key: 'chiefComplaint',
    label: 'Chief Complaint',
    required: true,
  },
  {
    key: 'backgroundAndOrPatientHistory',
    label: 'Background and/or Patient history',
  },
  {
    key: 'currentFinding',
    label: 'Current Findings',
  },
]

export const MULTI_NDEB_PATIENT_HISTORY_FIELDS: PatientBoxField[] = [
  {
    key: 'patient',
    label: 'Patient Description',
  },
  {
    key: 'chiefComplaint',
    label: 'Chief Complaint/Reason for Visit',
  },
  {
    key: 'backgroundAndOrPatientHistory',
    label: 'History of Chief Complaint', // note: reuse existing field
  },
  {
    key: 'dentalHistory',
    label: 'Dental History',
  },
  {
    key: 'medicalHistory',
    label: 'Medical History',
  },
  {
    key: 'currentFinding',
    label: 'Clinical Findings',
  },
]

export type AdditionalFieldsConfig = {
  patientBoxFields: PatientBoxField[]
  patientBoxLabel: string
  patientBoxAtTop: boolean
  patientBoxAsHtml: boolean
  patientBoxNeededByDefault: boolean
  tableFields: TableField[]
  otherFields: OtherFile[]
}

export const getAdditionalFieldsConfig = (questionType: QuestionTypeEnum): AdditionalFieldsConfig | null => {
  if (questionType === QuestionTypeEnum.MultiChoiceNDEB) {
    return {
      patientBoxFields: MULTI_NDEB_PATIENT_HISTORY_FIELDS,
      patientBoxLabel: 'Patient Box',
      patientBoxAtTop: true,
      patientBoxAsHtml: true,
      patientBoxNeededByDefault: false,
      tableFields: MULTI_NDEB_TABLE_FIELDS,
      otherFields: MULTI_NDEB_FILES,
    }
  }
  if (questionType === QuestionTypeEnum.SingleChoiceInbde) {
    return {
      patientBoxFields: PATIENT_HISTORY_FIELDS,
      patientBoxLabel: 'Patient Box',
      patientBoxAtTop: false,
      patientBoxAsHtml: true,
      patientBoxNeededByDefault: true,
      tableFields: [],
      otherFields: INBDE_FILES,
    }
  }
  if (questionType === QuestionTypeEnum.CaseNBDHE) {
    return {
      patientBoxFields: PATIENT_HISTORY_FIELDS,
      patientBoxLabel: 'Patient History',
      patientBoxAtTop: true,
      patientBoxAsHtml: true,
      patientBoxNeededByDefault: false,
      tableFields: [],
      otherFields: NBDHE_FILES,
    }
  }
  if (questionType === QuestionTypeEnum.RxWritingNDEB) {
    return {
      patientBoxFields: MULTI_NDEB_PATIENT_HISTORY_FIELDS,
      patientBoxLabel: 'Patient Box',
      patientBoxAtTop: true,
      patientBoxAsHtml: true,
      patientBoxNeededByDefault: true,
      tableFields: [],
      otherFields: [],
    }
  }
  if (questionType === QuestionTypeEnum.SingleChoiceDLOSCE || questionType === QuestionTypeEnum.MultiChoiceDLOSCE) {
    return {
      patientBoxFields: PATIENT_HISTORY_FIELDS,
      patientBoxLabel: 'Patient Box',
      patientBoxAtTop: true,
      patientBoxAsHtml: true,
      patientBoxNeededByDefault: true,
      tableFields: MULTI_DLOSCE_TABLE_FIELDS,
      otherFields: DLOSCE_FILES,
    }
  }
  return null
}

export type QuestionType = { id: QuestionTypeEnum; title: string }

export const QUESTION_TYPES: QuestionType[] = [
  {
    id: QuestionTypeEnum.SingleChoice,
    title: 'Generic - MCQ',
  },
  {
    id: QuestionTypeEnum.SingleChoiceInbde,
    title: 'INBDE Case - MCQ',
  },
  {
    id: QuestionTypeEnum.CaseNBDHE,
    title: 'NBDHE Case - MCQ',
  },
  {
    id: QuestionTypeEnum.TestletNBDHE,
    title: 'NBDHE Testlet - MCQ',
  },
  {
    id: QuestionTypeEnum.SingleChoiceCARS,
    title: 'MCAT - MCQ',
  },
  {
    id: QuestionTypeEnum.SingleChoiceNDEB,
    title: 'Standalone',
  },
  {
    id: QuestionTypeEnum.MultiChoiceNDEB,
    title: 'Multiselect - MCQ',
  },
  {
    id: QuestionTypeEnum.RxWritingNDEB,
    title: 'Rx. Writing',
  },
  {
    id: QuestionTypeEnum.SingleChoiceDLOSCE,
    title: 'DLOSCE - SingleSelect',
  },
  {
    id: QuestionTypeEnum.MultiChoiceDLOSCE,
    title: 'DLOSCE - MultiSelect',
  },
  {
    id: QuestionTypeEnum.SingleChoiceNBEO,
    title: 'NBEO - SingleSelect',
  },
  {
    id: QuestionTypeEnum.MultiChoiceNBEO,
    title: 'NBEO - MultiSelect',
  },
  {
    id: QuestionTypeEnum.SentenceEquivalence,
    title: 'Sentence Equivalent',
  },
  {
    id: QuestionTypeEnum.TextCompletion,
    title: 'Text Completion',
  },
  {
    id: QuestionTypeEnum.NumericResponse,
    title: 'Numerical Response',
  },
  {
    id: QuestionTypeEnum.SingleChoiceGRE,
    title: 'MC',
  },
  {
    id: QuestionTypeEnum.MultiChoiceGre,
    title: 'MMC',
  },
  {
    id: QuestionTypeEnum.PassageSingleChoiceGRE,
    title: 'Split-Screen MC',
  },
  {
    id: QuestionTypeEnum.PassageMultiChoiceGRE,
    title: 'Split-Screen MMC',
  },
]

// You should also add new QuestionTypeEnum value to getAvailableQuestionTypes for Question Picker settings,
// so it will be available for QB/PT/Quiz (src/pagesNew/QuestionBanks/utils.ts)
export const PLATFORMS_FOR_TYPES: Record<QuestionTypeEnum, PlatformSlug[]> = {
  [QuestionTypeEnum.SingleChoice]: [
    PlatformSlug.DATBooster,
    PlatformSlug.OATBooster,
    PlatformSlug.DATCrusher,
    PlatformSlug.INBDEBooster,
    PlatformSlug.NBDHEBooster,
    PlatformSlug.PATBooster,
    PlatformSlug.CasperBooster,
  ],
  [QuestionTypeEnum.SingleChoiceInbde]: [PlatformSlug.INBDEBooster],
  [QuestionTypeEnum.CaseNBDHE]: [PlatformSlug.NBDHEBooster],
  [QuestionTypeEnum.TestletNBDHE]: [PlatformSlug.NBDHEBooster],
  [QuestionTypeEnum.SingleChoiceCARS]: [PlatformSlug.CARSBooster],
  [QuestionTypeEnum.SingleChoiceNDEB]: [PlatformSlug.NDEBCrusher],
  [QuestionTypeEnum.MultiChoiceNDEB]: [PlatformSlug.NDEBCrusher],
  [QuestionTypeEnum.RxWritingNDEB]: [PlatformSlug.NDEBCrusher],
  [QuestionTypeEnum.SingleChoiceDLOSCE]: [PlatformSlug.DLOSCE],
  [QuestionTypeEnum.MultiChoiceDLOSCE]: [PlatformSlug.DLOSCE],
  [QuestionTypeEnum.SingleChoiceNBEO]: [PlatformSlug.NBEOPartOne],
  [QuestionTypeEnum.MultiChoiceNBEO]: [PlatformSlug.NBEOPartOne],
  [QuestionTypeEnum.SentenceEquivalence]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.TextCompletion]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.NumericResponse]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.SingleChoiceGRE]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.MultiChoiceGre]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.PassageSingleChoiceGRE]: [PlatformSlug.GREBooster],
  [QuestionTypeEnum.PassageMultiChoiceGRE]: [PlatformSlug.GREBooster],
}

export const DEFAULT_ANSWERS: Question['answer'] = [
  { ...EMPTY_ANSWER, isCorrect: true },
  ...new Array(4).fill(EMPTY_ANSWER),
]

export const PHARMACOLOGY_SUBJECT_SLUG = 'pharmacology'
